import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import { fetchCourseV2Content } from 'src/v3/classroom-v2/utils/api'
import { CourseV2Content } from 'src/v3/classroom-v2/types'

// Static, server-cacheable course content — safe to keep across navigations
// within a session (see index.tsx's uninitialized guard) since it doesn't
// change while a student is taking the course. Wizard-visit-scoped state
// (progress, exploration mode, mark-complete status) lives in
// classroom-v2-wizard.slice.ts instead, since that must be fresh every time
// the wizard is entered.

export let fetchCourseContent = createAsyncThunk(
  'classroomV2/fetchCourseContent',
  (courseID: string) => fetchCourseV2Content(courseID),
)

type ClassroomV2State = {
  courseContentStatus: Record<string, Status>
  courseContent: Record<string, CourseV2Content>
}

export let initialState: ClassroomV2State = {
  courseContentStatus: {},
  courseContent: {},
}

export let classroomV2Slice = createSlice({
  name: 'classroomV2',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourseContent.pending, (state, action) => {
        state.courseContentStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseContent.rejected, (state, action) => {
        state.courseContentStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseContent.fulfilled, (state, action) => {
        state.courseContentStatus[action.meta.arg] = 'fulfilled'
        state.courseContent[action.meta.arg] = action.payload
      })
  },
})

export let selectCourseContentByID = (id: string) => (state: AppRootState) =>
  state.classroomV2.courseContent[id]
export let selectCourseContentStatusByID =
  (id: string) => (state: AppRootState) =>
    state.classroomV2.courseContentStatus[id] ?? 'uninitialized'

export default classroomV2Slice.reducer
